import { PnlHierarchyMember } from 'src/components/star-grid/starGridTypes';

/**
 * Separator for a member's tree position (its root→member chain of ids).
 *
 * Rows are addressed by POSITION, not by member id: a multi-parent member appears once under each
 * parent, so `id` is not unique across the tree. Within a single parent ids ARE unique (the server
 * builds the tree as a trie keyed by id per level), so a path is a stable, collision-free row key —
 * which is why this needs none of the occurrence-suffix machinery a materialized-tree feed would.
 */
export const MEMBER_PATH_DELIMITER = '|||';

/** The row key for a member at one tree position. */
export const memberPathKey = (path: readonly string[]): string => path.join(MEMBER_PATH_DELIMITER);

/** Separator for a row's display path. Distinct from MEMBER_PATH_DELIMITER, which joins member ids. */
export const ROW_UID_DELIMITER = '>';

/** A row's unique id: the join of its display path, so it is stable and cannot collide. */
export const rowUid = (displayPath: readonly string[]): string => displayPath.join(ROW_UID_DELIMITER);

/** One rendered row of the member tree: a member at one position, with its depth. */
export interface MemberRow {
  /**
   * Unique per row, and the grid's row id.
   *
   * Not the position key: a member can hold the same position in several places at once — under its own
   * rollup and again inside a set `Parent` spliced in — so the position alone does not identify a row.
   */
  readonly uid: string;
  /** Position key — see MEMBER_PATH_DELIMITER. Addresses the MEMBER AT A POSITION, not the row. */
  readonly key: string;
  readonly id: string;
  readonly label: string;
  readonly isLeaf: boolean;
  /** 0 for a root; drives the indent rails. */
  readonly depth: number;
  /** Root→member ids, inclusive. The member's TRUE place in the hierarchy. */
  readonly path: readonly string[];
  /**
   * The chain of ROW KEYS from this row's displayed top down to itself — the tree path a grid
   * renders from.
   *
   * Deliberately not `path`. A grid with tree data invents a placeholder group row for any path
   * prefix it was given no data for, so true paths would resurrect exactly the intermediate
   * consolidations `Keep + Leaves` exists to remove, and would re-level everything to hierarchy
   * depth instead of depth-from-the-kept-node. Row keys also cannot collide, so a multi-parent
   * member keeps one row per parent instead of the two merging.
   */
  readonly displayPath: readonly string[];
  /**
   * True for a row `Parent` appended as a level-0 entry.
   *
   * It shares its position key with the occurrence it was relocated from, and the expansion seed is
   * keyed by that key — so without this flag an already-open original opened its new copy too, and
   * opening any one copy opened every other copy with it.
   */
  readonly added?: boolean;
  /**
   * The MEMBER has children in the dimension, but this ROW has none in the set — so the grid, which
   * derives the expander from the row set, would draw it as a leaf.
   *
   * Happens when a splice lands between a row and its children: `Parent` on a first child severs its
   * parent's whole run. Arc still shows that parent as expandable, because its expander comes from the
   * DIMENSION. Fed to `isRowMaster`, which is the one flag AG Grid honours for this — `isExpandable()`
   * is `hasChildren() || master`.
   */
  readonly canDrill?: boolean;
  /** Signed weight of the edge into this position — see `PnlHierarchyMember.weight`. */
  readonly weight?: number | null;
}

/**
 * Flatten the tree to the rows that are currently VISIBLE: a member shows when every ancestor
 * position is expanded. Depth-first, so row order is the hierarchy's own order.
 *
 * Everything starts collapsed, so this emits only the roots until the user expands — which is what
 * keeps a several-thousand-member hierarchy cheap to render without virtualization.
 */
export const visibleMemberRows = (members: readonly PnlHierarchyMember[], expanded: ReadonlySet<string>): MemberRow[] => {
  const rows: MemberRow[] = [];
  const walk = (nodes: readonly PnlHierarchyMember[], parentPath: readonly string[], parentKeys: readonly string[]): void => {
    for (const node of nodes) {
      const path = [...parentPath, node.id];
      const key = memberPathKey(path);
      const displayPath = [...parentKeys, key];
      rows.push({
        uid: rowUid(displayPath),
        key,
        id: node.id,
        label: node.label,
        isLeaf: node.is_leaf,
        depth: parentPath.length,
        path,
        displayPath,
        weight: node.weight
      });
      if (!node.is_leaf && expanded.has(key)) walk(node.children, path, displayPath);
    }
  };
  walk(members, [], []);
  return rows;
};

/** Total members counted as tree POSITIONS (a multi-parent member counts once per position). */
export const countMemberPositions = (members: readonly PnlHierarchyMember[]): number =>
  members.reduce((total, node) => total + 1 + countMemberPositions(node.children), 0);

// ─── Subset operations (TM1 subset-editor semantics) ─────────────────────────

/** One tree position, flattened for O(1) lookup by the subset operations. */
export interface MemberIndexEntry {
  readonly key: string;
  readonly id: string;
  readonly label: string;
  readonly isLeaf: boolean;
  readonly depth: number;
  readonly path: readonly string[];
  readonly childKeys: readonly string[];
  /** Signed weight of the edge into this position — see `PnlHierarchyMember.weight`. */
  readonly weight?: number | null;
  /** Depth-first position in the whole tree — the hierarchy's own order. */
  readonly order: number;
}

export type MemberIndex = ReadonlyMap<string, MemberIndexEntry>;

/** Flatten the tree to a position→entry index, recording depth-first order. */
export const indexMemberTree = (members: readonly PnlHierarchyMember[]): MemberIndex => {
  const index = new Map<string, MemberIndexEntry>();
  let order = 0;
  const walk = (nodes: readonly PnlHierarchyMember[], parentPath: readonly string[]): void => {
    for (const node of nodes) {
      const path = [...parentPath, node.id];
      const key = memberPathKey(path);
      const childKeys = node.children.map((child) => memberPathKey([...path, child.id]));
      index.set(key, {
        key,
        id: node.id,
        label: node.label,
        isLeaf: node.is_leaf,
        depth: parentPath.length,
        path,
        childKeys,
        weight: node.weight,
        order: order++
      });
      walk(node.children, path);
    }
  };
  walk(members, []);
  return index;
};

/**
 * Every position below `key`, all levels, consolidations included, in HIERARCHY order.
 *
 * Pre-order depth-first, not a stack pop: a LIFO walk returns the subtree reversed, which silently
 * reversed the leaf order everything downstream displays.
 */
export const descendantKeys = (index: MemberIndex, key: string): string[] => {
  const out: string[] = [];
  const walk = (parent: string): void => {
    for (const child of index.get(parent)?.childKeys ?? []) {
      out.push(child);
      walk(child);
    }
  };
  walk(key);
  return out;
};

/**
 * The leaf positions under `key`.
 *
 * A leaf is its OWN leaf set, so `Keep Leaves` on a leaf keeps it rather than deleting it — the
 * alternative silently empties the subset when someone right-clicks a bottom-level member.
 */
export const leafKeysUnder = (index: MemberIndex, key: string): string[] => {
  const entry = index.get(key);
  if (!entry) return [];
  if (entry.isLeaf) return [key];
  return descendantKeys(index, key).filter((candidate) => index.get(candidate)?.isLeaf);
};

/**
 * The subset operations, as named in the TM1 subset editor.
 *
 * Each one keeps the clicked node as the new TOP of the pane and fixes how much of its subtree is
 * shown; the displayed level is therefore relative to that node, not the hierarchy root. That is
 * the "levels auto-update" behaviour: Keep + Leaves on Contribution Profit puts it at level 0 with
 * its leaves at level 1, however deep they really sit.
 *
 *   * `keep`             — the node, subtree intact but COLLAPSED (expandable).
 *   * `keepChildren`     — the node expanded one level; each child still holds its own rollup.
 *   * `keepDescendants`  — the node expanded all the way down to its leaves.
 *   * `keepLeaves`       — "Keep + Leaves": the node plus its leaves, with NO intermediate
 *                        consolidations between them.
 *   * `leavesOnly`       — "Keep Leaves": its leaves alone; the node itself is not shown. The
 *                        internal key stays `leavesOnly` precisely because the two USER-FACING
 *                        names differ by one plus sign and are trivial to confuse in code.
 */
export type MemberSubsetOp = 'keep' | 'keepChildren' | 'keepDescendants' | 'keepLeaves' | 'leavesOnly';

export const MEMBER_SUBSET_OP_LABELS: Readonly<Record<MemberSubsetOp, string>> = {
  keep: 'Keep',
  keepChildren: 'Keep + Children',
  keepDescendants: 'Keep + Descendants',
  keepLeaves: 'Keep + Leaves',
  leavesOnly: 'Keep Leaves'
};

/** A built subset: which nodes anchor the pane, and how much of each subtree it shows. */
export interface MemberSubsetScope {
  /** Position keys, hierarchy order, none a descendant of another. */
  readonly anchors: readonly string[];
  readonly op: MemberSubsetOp;
}

/**
 * Drop keys that sit under another key in the same set, keeping only the TOPMOST.
 *
 * Needed wherever a cascaded selection is the input: ticking a rollup selects its whole subtree
 * (`groupSelects: 'descendants'`), so an operation that treats every ticked row as a target would act
 * on dozens of descendants the user never picked.
 */
export const topmostKeys = (index: MemberIndex, anchors: readonly string[]): string[] => {
  /*
   * A key the index does not know is DROPPED, leaving the operation with no anchors, which the reducer then
   * declines outright — better a no-op than an emptied pane.
   *
   * An operation's target has to exist in the dimension. Verified in Arc, and implied by the MDX it emits:
   * every operation names its target as a dimension member reference (`[Dim].[Hier].[Parent^Child]`), so a
   * member the hierarchy has no position for — an imported view's `absentPosition` row — is not something an
   * operation can be pointed at. Those rows are managed with Remove instead.
   */
  const kept = anchors.filter((anchor) => index.has(anchor));
  const nested = kept
    .filter((anchor) => {
      const path = index.get(anchor)?.path ?? [];
      return !kept.some((other) => {
        if (other === anchor) return false;
        const otherPath = index.get(other)?.path ?? [];
        return otherPath.length < path.length && memberPathKey(path.slice(0, otherPath.length)) === other;
      });
    })
    .sort((left, right) => (index.get(left)?.order ?? 0) - (index.get(right)?.order ?? 0));
  return nested;
};

export const buildMemberSubsetScope = (index: MemberIndex, op: MemberSubsetOp, selected: readonly string[]): MemberSubsetScope => ({
  anchors: topmostKeys(index, selected),
  op
});

/**
 * The expansion each operation opens with. Expansion is what distinguishes `keep` from
 * `keepDescendants` — they scope the same subtree, one closed and one open — so it has to be seeded
 * by the operation rather than left at whatever the user had expanded while browsing.
 */
export const seedExpansion = (index: MemberIndex, scope: MemberSubsetScope): Set<string> => {
  const expanded = new Set<string>();
  if (scope.op === 'keep' || scope.op === 'leavesOnly') return expanded;
  for (const anchor of scope.anchors) {
    expanded.add(anchor);
    // Descendants opens the whole subtree; children and leaves open only the anchor.
    if (scope.op === 'keepDescendants') for (const key of descendantKeys(index, anchor)) expanded.add(key);
  }
  return expanded;
};

// ─── Search ──────────────────────────────────────────────────────────────────

/** Every position, for the "reveal everything" pass search needs. */
export const allPositionKeys = (index: MemberIndex): ReadonlySet<string> => new Set(index.keys());

/** What a search leaves on screen: the surviving rows, and which of them start open. */
export interface MemberSearchResult {
  readonly rows: MemberRow[];
  /** Ancestors of the hits — opened so the hit is visible. Hits themselves stay closed. */
  readonly expand: ReadonlySet<string>;
}

/**
 * Narrow rows to the members matching `term`, keeping
 *   * each match's ROLLUP (the ancestors above it), so a hit is shown in the branch it belongs to;
 *   * each match's own SUBTREE, so a hit is still expandable — filtering a match's children away
 *     stripped its expander and made it impossible to see what rolls into it.
 * Non-matching branches disappear entirely.
 *
 * Only the ancestors are opened. The matches keep their subtrees collapsed, so a search for a
 * high-level rollup does not dump thousands of descendants on screen.
 *
 * Ancestors and descendants are both resolved through `displayPath`, never the hierarchy `path`.
 * Those coincide while browsing but not inside a built scope, where the display chain is whatever the
 * operation decided (`Keep + Leaves` hangs a leaf straight off its anchor) — using hierarchy paths
 * there kept rows the grid has no node for and dropped ones it does, leaving it to invent
 * placeholder rows.
 *
 * Matches on label and on member id, because ids are what analysts paste in and labels collide.
 *
 * Addresses rows by UID, not by position key, so it must be given `entryRows` output — whose display
 * paths are uid chains. A position key does not identify a row: the same member at the same position
 * can be on screen twice at once, once in its own rollup and again inside a set `Parent` spliced in,
 * and keying on it made one occurrence's ancestors resolve against the other's.
 */
export const searchMemberRows = (rows: readonly MemberRow[], term: string): MemberSearchResult => {
  const needle = term.trim().toLowerCase();
  if (needle.length === 0) return { rows: [...rows], expand: new Set<string>() };

  const matched = new Set<string>();
  for (const row of rows) {
    if (row.label.toLowerCase().includes(needle) || row.id.toLowerCase().includes(needle)) matched.add(row.uid);
  }

  const keep = new Set<string>(matched);
  const expand = new Set<string>();
  for (const row of rows) {
    if (matched.has(row.uid)) {
      for (const segment of row.displayPath.slice(0, -1)) {
        keep.add(segment);
        expand.add(segment);
      }
      continue;
    }
    // A descendant of a hit: keep it so the hit can be expanded into.
    if (row.displayPath.some((segment) => matched.has(segment))) keep.add(row.uid);
  }
  return { rows: rows.filter((row) => keep.has(row.uid)), expand };
};

// ─── Removal ─────────────────────────────────────────────────────────────────

// ─── Flat view (TM1SUBSETALL) ────────────────────────────────────────────────

/**
 * Every DISTINCT member as a row at level 0, each still EXPANDABLE into its own rollup — the `All`
 * view.
 *
 * Mirrors Prophecy's `TM1SUBSETALL(...)`: the dimension's element count exactly once each with no
 * hierarchy imposed (160 rows for FPA Deck Country), but every row keeps its ⊞ so a member's children
 * can be opened underneath it. Contrast `.Members`, which walks the hierarchy and yields 306 rows for
 * the same dimension because a multi-parent member appears once per parent — that is the fully
 * expanded TREE (`Show All Members`), not this.
 *
 * A member with several positions collapses to ONE flat row, keyed by its first position: flattening
 * removes the parent, so two copies would be indistinguishable on screen.
 *
 * Children of a flat row are always emitted, even while it is collapsed. A grid only draws an
 * expander for a row it can already see children for, so withholding them until expansion left every
 * row looking like a leaf. The grid hides children of a collapsed parent itself, so this costs
 * rendering nothing — and only ONE level is emitted ahead, which is what keeps the row set bounded
 * rather than materialising every member's whole subtree.
 *
 * Ordered by label. TM1 returns insertion order, which the closure does not carry — `sort_order` is
 * sibling order within a parent, not dimension registration order — so this cannot match Prophecy's
 * row order exactly, and alphabetical at least makes a member findable.
 */
export const flatMemberRows = (index: MemberIndex, expanded: ReadonlySet<string>): MemberRow[] => {
  const byMember = new Map<string, MemberIndexEntry>();
  for (const entry of index.values()) {
    if (!byMember.has(entry.id)) byMember.set(entry.id, entry);
  }
  const roots = [...byMember.values()].sort(
    (left, right) => left.label.toLowerCase().localeCompare(right.label.toLowerCase()) || left.id.localeCompare(right.id)
  );

  const rows: MemberRow[] = [];
  for (const root of roots) emitRootWithSubtree(index, root, expanded, rows);
  return rows;
};

/**
 * Emit one member as a level-0 row plus its subtree, ONE LEVEL AHEAD of what is open.
 *
 * A grid only draws an expander for a row it can already see children for, so the level ahead is what
 * keeps the ⊞ — and stopping there is what keeps the row set bounded instead of materialising every
 * member's whole subtree. The grid hides children of a collapsed parent itself, so the extra rows cost
 * nothing to render.
 */
const emitRootWithSubtree = (index: MemberIndex, root: MemberIndexEntry, expanded: ReadonlySet<string>, rows: MemberRow[]): void => {
  const emit = (entry: MemberIndexEntry, depth: number, parentPath: readonly string[]): void => {
    const displayPath = [...parentPath, entry.key];
    rows.push({
      uid: rowUid(displayPath),
      key: entry.key,
      id: entry.id,
      label: entry.label,
      isLeaf: entry.isLeaf,
      depth,
      path: entry.path,
      displayPath,
      weight: entry.weight
    });
    if (depth > 0 && !expanded.has(entry.key)) return;
    for (const childKey of entry.childKeys) {
      const child = index.get(childKey);
      if (child) emit(child, depth + 1, displayPath);
    }
  };
  emit(root, 0, []);
};

// ─── Parent (added roots) ────────────────────────────────────────────────────

/**
 * Every distinct parent POSITION of the member at `key`.
 *
 * Keyed on the member ID, not on the position clicked: a member sitting under several parents has one
 * position per parent, so grouping by id is what returns ALL of them rather than just the branch the
 * user happened to right-click in. A root has no parent and contributes nothing.
 */
export const parentKeysOf = (index: MemberIndex, key: string): string[] => {
  const id = index.get(key)?.id;
  if (!id) return [];
  /*
   * Deduped by the parent's member ID, not by its position.
   *
   * One logical parent reachable at several positions — itself a multi-parent member — otherwise came
   * back once per position, so `Parent` listed "Contribution Profit" three times. TM1 returns one entry
   * per distinct parent MEMBER, which is what the three rows in `TM1ROLLUP`'s output are.
   */
  const byParentId = new Map<string, string>();
  for (const entry of index.values()) {
    if (entry.id !== id || entry.path.length < 2) continue;
    const parentKey = memberPathKey(entry.path.slice(0, -1));
    const parentId = index.get(parentKey)?.id;
    if (!parentId || byParentId.has(parentId)) continue;
    byParentId.set(parentId, parentKey);
  }
  return [...byParentId.values()];
};
